import { useState } from 'react';
import { Modal, ModalBody, ModalFooter, Button } from 'reactstrap';
import WarningIcon from '@mui/icons-material/Warning';
import { create } from 'zustand';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import useDeleteTrap from 'hooks/Traps/Trap/useDeleteTrap';
import Toggle from 'components/Common/Toggle';

interface TrapDeleteModalProps {
  trapPk: number;
  trapName?: string;
  isOpen: boolean;
  onPlan?: boolean;
  closeDeleteTrapModal: () => void;
  openDeleteTrapModal: (trapPk: number, trapSerialNumber: string, onPlan?: boolean) => void;
}

interface TrapDeleteModalRefetchProps {
  refetchTraps: () => void;
  refetchWarehouse?: () => void;
}

export const useTrapDeleteModalStore = create<TrapDeleteModalProps>(set => ({
  isOpen: false,
  trapName: '',
  trapPk: 0,
  onPlan: true,
  openDeleteTrapModal: (trapPk: number, trapName: string) => set({ isOpen: true, trapName, trapPk, onPlan: true }),
  closeDeleteTrapModal: () => set({ isOpen: false, trapName: '', trapPk: 0, onPlan: true }),
}));

const TrapDeleteModal: React.FC<TrapDeleteModalRefetchProps> = ({ refetchTraps, refetchWarehouse }) => {
  const [level, setLevel] = useState(-1);
  const { isOpen, trapName, trapPk, onPlan, closeDeleteTrapModal: closeModal } = useTrapDeleteModalStore();
  const { getDeleteById } = useDeleteTrap();

  const { t } = useTranslation();

  const onReset = () => {
    setLevel(-1);
    closeModal();
  };

  const onDelete = () => {
    getDeleteById(
      { trapId: trapPk, level },
      {
        onSuccess: () => {
          enqueueSnackbar('Trap deleted', {
            variant: 'success',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          onReset();
          refetchTraps();
          refetchWarehouse && refetchWarehouse();
        },
        onError: () => {
          enqueueSnackbar('Error deleting trap', {
            variant: 'error',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          onReset();
          refetchTraps();
          refetchWarehouse && refetchWarehouse();
        },
      },
    );
  };

  return (
    <div>
      <Modal isOpen={isOpen} toggle={onReset} size="md">
        <ModalBody>
          <div className="mt-4 pt-4 d-flex flex-column  justify-content-center">
            <div className="d-flex align-items-center justify-content-center">
              <WarningIcon color="warning" fontSize="large" style={{ width: '100px', height: '100px' }} />
            </div>
            <h4 className="text-center">{`${t('TrapModals.deleteModal.trapDelete')} ${trapName}`}</h4>
            <p className="text-center my-2 text-muted">{t('TrapModals.deleteModal.choseDeleteOption')}</p>
            <div className="p-4">
              {onPlan && (
                <>
                  <div className="d-flex align-items-center mb-3">
                    <Toggle
                      checked={level >= 1}
                      disabled={level > 1}
                      onChange={isChecked => setLevel(isChecked ? 1 : 0)}
                      size="sm"
                    />
                    <div className="ms-3">
                      <p className="mb-0 pb-0 fw-medium">{t('TrapModals.deleteModal.firstOption')}</p>
                      <p className="text-muted mb-0">{t('TrapModals.deleteModal.firstOption__comment')}</p>
                    </div>
                  </div>

                  <div className="d-flex align-items-center mb-3">
                    <Toggle
                      checked={level >= 2}
                      disabled={level > 2}
                      onChange={isChecked => setLevel(isChecked ? 2 : 0)}
                      size="sm"
                    />
                    <div className="ms-3">
                      <p className="mb-0 pb-0 fw-medium">{t('TrapModals.deleteModal.secondOption')}</p>
                      <p className="text-muted mb-0">{t('TrapModals.deleteModal.secondOption__comment')}</p>
                    </div>
                  </div>

                  <div className="d-flex align-items-center mb-3">
                    <Toggle
                      checked={level >= 3}
                      disabled={false}
                      onChange={isChecked => setLevel(isChecked ? 3 : 0)}
                      size="sm"
                    />
                    <div className="ms-3">
                      <p className="mb-0 pb-0 fw-medium">{t('TrapModals.deleteModal.thirdOption')}</p>
                      <p className="text-muted mb-0">{t('TrapModals.deleteModal.thirdOption__comment')}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          <Button
            color="danger"
            onClick={() => {
              onDelete();
            }}
            {...(level < 1 && { disabled: true })}
          >
            {t('TrapModals.deleteModal.remove')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default TrapDeleteModal;
